import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { AiRunner } from '../../game/AiRunner';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { t } from '../../game/i18n';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { pickOpponents } from '../../game/data/aiRoster';
import type { DisciplineResult, Standing } from '../../game/types';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Phase = 'intro' | 'running' | 'finished';
type Side = 'L' | 'R';

const START_X = 220; // world-space start (px)

/**
 * 100m Sprint. Pure button-mash with rhythm. Responsive: ground line, HUD and athlete
 * size are derived from the live viewport so it fills the screen on any device.
 */
export class Sprint100m extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;
  private ai: AiRunner[] = [];

  private velocity = 0;
  private distance = 0;
  private elapsed = 0;
  private acc = 0;
  private topSpeed = 0;
  private perfectTaps = 0;
  private wrTime = 0;
  private wrLine!: Phaser.GameObjects.Graphics;

  private lastSide: Side | null = null;
  private lastTapAt = 0;
  private cadence = 0;

  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;

  // Layout (set in create from the viewport).
  private W = 0;
  private H = 0;
  private groundY = 0;
  private anchorX = 0;
  private uiS = 1;
  private lanes: number[] = []; // feet Y per lane (0 = player, front/bottom)
  private trackTopY = 0;

  private speedBar!: PowerMeter;
  private cadenceBar!: PowerMeter;
  private hudTime!: Phaser.GameObjects.Text;
  private hudDist!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private dust!: Phaser.GameObjects.Particles.ParticleEmitter;
  private beatTimer?: Phaser.Time.TimerEvent;
  private tapZones!: TapZones;

  // P0 "real race" feel.
  private crowd!: Phaser.GameObjects.TileSprite;
  private speedFx!: Phaser.GameObjects.Graphics;
  private fxPhase = 0;
  private combo = 0;
  private boostUntil = 0;
  private leaned = false;
  private comboText!: Phaser.GameObjects.Text;
  private boostText!: Phaser.GameObjects.Text;

  constructor() {
    super('Sprint100m');
  }

  create(): void {
    super.create();
    // Phaser reuses the scene instance across scene.start, so field initializers do NOT
    // re-run on replay — reset all mutable race state here or "Rigioca" starts finished.
    this.phase = 'intro';
    this.velocity = 0;
    this.distance = 0;
    this.elapsed = 0;
    this.acc = 0;
    this.topSpeed = 0;
    this.perfectTaps = 0;
    this.lastSide = null;
    this.lastTapAt = 0;
    this.cadence = 0;
    this.ai = [];
    this.combo = 0;
    this.boostUntil = 0;
    this.leaned = false;
    this.fxPhase = 0;

    const { w, h, s } = viewport(this);
    this.W = w;
    this.H = h;
    this.uiS = s;
    this.anchorX = w * 0.28;
    // 4 lanes (player + 3 AI) stacked with depth; the track fills from the back lane
    // down so every runner stands on it. Crowd sits above the horizon (trackTopY).
    const laneCount = 4;
    const bottomY = h - Math.max(64, h * 0.09);
    const laneGap = Math.max(38, h * 0.06);
    this.lanes = Array.from({ length: laneCount }, (_, i) => bottomY - i * laneGap);
    this.groundY = this.lanes[0]!;
    this.trackTopY = this.lanes[laneCount - 1]! - laneGap * 0.7;
    this.wrLine = this.add.graphics().setDepth(3);
    this.fetchWorldRecord('sprint_100m', 'time', (m) => { this.wrTime = m; });

    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;

    this.addStadium();
    this.drawTrack();
    this.buildAthlete();
    this.buildHud();
    this.bindInput();
    this.startCountdown();
  }

  private addStadium(): void {
    // Sky gradient behind everything.
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-12);
    sky.fillStyle(PALETTE.sky, 1).fillRect(0, 0, this.W, this.trackTopY + 40);
    // Crowd as a parallax tile-sprite: drifts slowly vs the foreground (depth cue).
    const src = this.textures.get('stadium').getSourceImage();
    const scale = (this.trackTopY + 50) / src.height;
    this.crowd = this.add.tileSprite(0, 0, this.W, this.trackTopY + 50, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10);
    this.crowd.setTileScale(scale, scale);
  }

  private drawTrack(): void {
    // --- Screen-fixed multi-lane track surface (every lane stands on it). ---
    const t = this.add.graphics().setScrollFactor(0).setDepth(-6);
    // Green apron + bright kerb at the horizon to hide the stadium seam.
    t.fillStyle(0x3f7d4f, 1).fillRect(0, this.trackTopY - 12, this.W, 16);
    t.fillStyle(PALETTE.laneAccent, 0.9).fillRect(0, this.trackTopY, this.W, 4);
    // Track body with alternating lane shading for depth.
    t.fillStyle(PALETTE.track, 1).fillRect(0, this.trackTopY + 4, this.W, this.H - this.trackTopY);
    for (let i = this.lanes.length - 1; i >= 0; i--) {
      const top = i === this.lanes.length - 1 ? this.trackTopY + 4 : this.lanes[i + 1]!;
      const shade = i % 2 === 0 ? 0x2f3a63 : PALETTE.track;
      t.fillStyle(shade, 1).fillRect(0, top, this.W, this.lanes[i]! - top + 2);
      // Lane line at each runner's feet.
      t.fillStyle(PALETTE.trackLine, 0.6).fillRect(0, this.lanes[i]!, this.W, 2);
    }

    // --- World-space distance markers + finish (scroll to convey speed). ---
    const endX = START_X + (SPRINT.raceMeters + 6) * SPRINT.pixelsPerMeter;
    const g = this.add.graphics().setDepth(-5);
    for (let m = 0; m <= SPRINT.raceMeters; m += 10) {
      const x = START_X + m * SPRINT.pixelsPerMeter;
      g.fillStyle(PALETTE.trackLine, 0.7).fillRect(x, this.trackTopY + 4, 3, this.groundY - this.trackTopY);
      this.add.text(x, this.groundY + 16, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '16px', color: '#aeb8db' }).setOrigin(0.5, 0);
    }
    const fx = START_X + SPRINT.raceMeters * SPRINT.pixelsPerMeter;
    const fh = this.groundY - this.trackTopY;
    const cells = Math.max(6, Math.round(fh / 26));
    for (let i = 0; i < cells; i++) {
      g.fillStyle(i % 2 === 0 ? PALETTE.white : PALETTE.ink, 1);
      g.fillRect(fx - 7, this.trackTopY + 4 + i * (fh / cells), 14, fh / cells);
    }
    this.add.text(fx, this.trackTopY - 16, '🏁', { fontSize: '36px' }).setOrigin(0.5);

    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildAthlete(): void {
    const displayH = Phaser.Math.Clamp(this.H * 0.34, 170, 320);

    // AI opponents in the lanes behind the player (smaller + dimmer = further back).
    const opponents = pickOpponents(getSkin(this.athlete.skin).id, this.lanes.length - 1);
    opponents.forEach((opp, i) => {
      const lane = i + 1;
      const laneY = this.lanes[lane]!;
      const aiH = displayH * (1 - lane * 0.12);
      const finishTime = Math.max(8.5, opp.sprintTime + (Math.random() * 2.4 - 0.8));
      const runner = new AiRunner(this, opp, finishTime, laneY, aiH, START_X, SPRINT.raceMeters, SPRINT.pixelsPerMeter);
      runner.view.container.setDepth(5 - lane).setAlpha(0.92 - lane * 0.05);
      this.ai.push(runner);
    });

    // Player in the front lane, on top.
    this.rig = createAthleteView(this, START_X, this.groundY, this.athlete, displayH);
    this.rig.container.setDepth(6);
    this.dust = this.add.particles(0, 0, 'spark', {
      lifespan: 420,
      speed: { min: 40, max: 130 },
      angle: { min: 150, max: 210 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.7, end: 0 },
      tint: PALETTE.track,
      gravityY: 380,
      emitting: false,
    });
    this.dust.setDepth(-1);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hudTime = this.add.text(this.W / 2, 16, '0.00"', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(40), color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    this.hudDist = this.add.text(this.W / 2, 16 + 44 * this.uiS, '0 / 100 m', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(20), color: '#d3dbf2',
    }).setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 2, '#000000aa', 4);

    const barW = Math.min(320, this.W * 0.4);
    const barY = this.H - 34 * this.uiS;
    this.speedBar = new PowerMeter(this, 30 + barW / 2, barY, { width: barW, height: 20 });
    this.add.text(30 + barW / 2, barY - 24 * this.uiS, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(13), color: '#d3dbf2' })
      .setOrigin(0.5).setScrollFactor(0);

    const tMin = SPRINT.idealCadenceMin / SPRINT.overspinThreshold;
    const tMax = SPRINT.idealCadenceMax / SPRINT.overspinThreshold;
    this.cadenceBar = new PowerMeter(this, this.W - 30 - barW / 2, barY, {
      width: barW, height: 20, target: [tMin, tMax], fillColor: PALETTE.cadence,
    });
    this.add.text(this.W - 30 - barW / 2, barY - 24 * this.uiS, t('hud.rhythm'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(13), color: '#d3dbf2' })
      .setOrigin(0.5).setScrollFactor(0);

    this.tapZones = new TapZones(this);

    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(110), color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);

    // Speed-lines overlay (drawn each frame at high velocity).
    this.speedFx = this.add.graphics().setScrollFactor(0).setDepth(18);
    // Combo + boost readouts.
    this.comboText = this.add.text(this.W / 2, 16 + 70 * this.uiS, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(22), color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 4);
    this.boostText = this.add.text(this.W / 2, this.H * 0.26, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(56), color: '#ff7a59', fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setShadow(0, 6, '#00000088', 10).setAlpha(0);
  }

  private bindInput(): void {
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
  }

  private startCountdown(): void {
    const seq = ['3', '2', '1', t('game.go')];
    seq.forEach((label, i) => {
      this.time.delayedCall(i * 800, () => {
        this.bigText.setText(label).setScale(1.6).setAlpha(1);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 400, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.vibrate(HAPTICS.go);
          this.phase = 'running';
          this.controls.setEnabled(true);
          this.lastTapAt = this.time.now / 1000;
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 350, duration: 300 });
          this.startBeat();
        } else {
          AudioEngine.countdownTick();
          this.vibrate(HAPTICS.countdownTick);
        }
      });
    });
  }

  private startBeat(): void {
    const idealHz = (SPRINT.idealCadenceMin + SPRINT.idealCadenceMax) / 2;
    this.beatTimer = this.time.addEvent({
      delay: 1000 / idealHz,
      loop: true,
      callback: () => {
        if (this.phase === 'running') AudioEngine.beat();
      },
    });
  }

  private onTap(side: Side): void {
    if (this.phase !== 'running') return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    const inst = 1 / dt;
    this.cadence = Phaser.Math.Linear(this.cadence, inst, 0.45);
    this.lastTapAt = now;

    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    let quality = alternated ? 0.5 : 0.1;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) {
      mult *= SPRINT.flowBonus;
      quality = 1;
      this.perfectTaps++;
    } else if (this.cadence > SPRINT.overspinThreshold) {
      mult *= SPRINT.overspinPenalty;
      quality = 0.2;
    }

    // Combo: perfect rhythm builds it; a sloppy tap (same side / overspin) breaks it.
    if (quality >= 1) {
      this.combo++;
      if (this.combo >= 6) {
        const wasBoosting = now < this.boostUntil;
        this.boostUntil = now + 1.6; // sustained perfect rhythm keeps the boost alive
        if (!wasBoosting) this.activateBoost();
      }
    } else if (quality < 0.5) {
      this.combo = 0;
    }

    const boosting = now < this.boostUntil;
    const boostMult = boosting ? 1.6 : 1;
    const impulse = SPRINT.tapImpulse * this.impulseScale * mult * boostMult;
    const cap = this.maxSpeed * (boosting ? 1.25 : 1.06);
    this.velocity = Math.min(cap, this.velocity + impulse);

    // Finish lean ("tuffo"): a one-time forward dive burst in the last metres.
    if (!this.leaned && this.distance >= SPRINT.raceMeters - 6) {
      this.leaned = true;
      this.velocity = Math.min(this.maxSpeed * 1.3, this.velocity + 1.4);
      this.rig.container.setAngle(-12);
      this.bigText.setText(t('game.dive')).setFontSize(Math.round(56 * this.uiS)).setColor('#59d2ff').setAlpha(1).setScale(0.6);
      this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
      this.cameras.main.shake(120, 0.004);
    }

    this.tapZones.flash(side);
    this.feedbackTap(quality >= 1 && boosting ? 1.2 : quality);
  }

  private activateBoost(): void {
    AudioEngine.go();
    this.vibrate(HAPTICS.goodTap);
    this.cameras.main.flash(180, 255, 140, 60);
    this.cameras.main.shake(180, 0.004);
    this.boostText.setText('🔥 BOOST!').setAlpha(1).setScale(0.5);
    this.tweens.add({ targets: this.boostText, scale: 1.1, duration: 300, ease: 'Back.out' });
    this.tweens.add({ targets: this.boostText, alpha: 0, delay: 700, duration: 400 });
    this.dust.emitParticleAt(this.rig.container.x - 10, this.groundY - 4, 24);
  }

  private feedbackTap(quality: number): void {
    AudioEngine.tap(quality);
    this.vibrate(quality >= 1 ? HAPTICS.goodTap : HAPTICS.tap);
    this.dust.emitParticleAt(this.rig.container.x - 10, this.groundY - 4, quality >= 1 ? 6 : 3);
    if (quality >= 1) this.cameras.main.shake(60, 0.002);
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    this.tapZones.update(dt);
    // AI keep idling/running every frame (elapsed is 0 until the gun).
    for (const a of this.ai) a.update(this.elapsed, dt);
    if (this.phase === 'intro') {
      this.rig.update(dt);
      return;
    }
    if (this.phase === 'running') {
      this.acc += dt;
      let steps = 0;
      while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
        this.stepSim(SIM_STEP);
        this.acc -= SIM_STEP;
        steps++;
      }
      const sinceTap = this.time.now / 1000 - this.lastTapAt;
      if (sinceTap > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
    }
    this.renderFrame(dt);
  }

  /** Faint gold pace line moving at the world-record time — stay ahead of it to beat it. */
  private drawWr(): void {
    const g = this.wrLine;
    g.clear();
    if (this.wrTime <= 0) return;
    const p = Math.min(1, this.elapsed / this.wrTime);
    const x = START_X + SPRINT.raceMeters * SPRINT.pixelsPerMeter * p;
    g.fillStyle(PALETTE.sun, 0.4).fillRect(x - 1.5, this.trackTopY, 3, this.groundY - this.trackTopY + 12);
    g.fillStyle(PALETTE.sun, 0.9).fillTriangle(x - 7, this.trackTopY - 2, x + 7, this.trackTopY - 2, x, this.trackTopY + 8);
  }

  private stepSim(step: number): void {
    this.velocity *= SPRINT.dragPerStep;
    if (this.velocity < 0.01) this.velocity = 0;
    this.distance += this.velocity * step;
    this.elapsed += step;
    this.topSpeed = Math.max(this.topSpeed, this.velocity);
    if (this.distance >= SPRINT.raceMeters) {
      this.distance = SPRINT.raceMeters;
      this.finish();
    }
  }

  private renderFrame(dt: number): void {
    const worldX = START_X + this.distance * SPRINT.pixelsPerMeter;
    this.rig.container.x = worldX;
    this.cameras.main.scrollX = Phaser.Math.Clamp(worldX - this.anchorX, 0, this.cameras.main.getBounds().width - this.W);

    const speedNorm = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(this.phase === 'finished' ? 0 : speedNorm);
    this.rig.update(dt);

    // Parallax crowd drift + speed-lines + boost dust.
    this.crowd.tilePositionX = this.cameras.main.scrollX * 0.12;
    const boosting = this.time.now / 1000 < this.boostUntil;
    this.drawSpeedLines(boosting ? Math.max(speedNorm, 0.8) : speedNorm, dt);
    if (boosting && this.phase === 'running') this.dust.emitParticleAt(this.rig.container.x - 10, this.groundY - 4, 2);

    this.speedBar.set(speedNorm);
    const cn = Phaser.Math.Clamp(this.cadence / SPRINT.overspinThreshold, 0, 1);
    this.cadenceBar.set(cn, cn);
    this.hudTime.setText(`${this.elapsed.toFixed(2)}"`);
    this.hudDist.setText(`${Math.floor(this.distance)} / ${SPRINT.raceMeters} m`);
    this.drawWr();
    this.comboText.setText(this.combo >= 3 ? t('game.comboX', { n: this.combo }) : '').setColor(boosting ? '#ff7a59' : '#ffd24a');
  }

  private drawSpeedLines(intensity: number, dt: number): void {
    this.speedFx.clear();
    if (intensity <= 0.45) return;
    const a = (intensity - 0.45) / 0.55;
    this.fxPhase += dt * (600 + intensity * 1400);
    for (let i = 0; i < 14; i++) {
      const y = (i * 89) % this.H;
      const len = 50 + (i % 3) * 50;
      const x = this.W - ((this.fxPhase + i * 167) % (this.W + 260));
      this.speedFx.fillStyle(0xffffff, 0.13 * a).fillRect(x, y, len, 2);
    }
  }

  private finish(): void {
    if (this.phase === 'finished') return;
    this.phase = 'finished';
    this.controls.setEnabled(false);
    this.beatTimer?.remove();
    this.tapZones.setVisible(false);
    this.speedFx.clear();
    this.comboText.setText('');
    this.rig.container.setAngle(0);
    this.rig.setPose('celebrate');
    AudioEngine.fanfare();
    this.vibrate(HAPTICS.finish);
    this.cameras.main.shake(220, 0.006);
    this.confetti();

    // Final standings: player time + each AI's (fixed) finish time, sorted fastest-first.
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.elapsed },
      ...this.ai.map((a) => ({ name: a.opponent.name, species: a.opponent.species, isPlayer: false, nationality: a.opponent.nationality, metric: a.finishTime })),
    ].sort((x, y) => x.metric - y.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;

    const score = Math.round(Phaser.Math.Clamp(1400 - this.elapsed * 60, 200, 1000));
    const reward = getDiscipline('sprint_100m').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'sprint_100m',
      metricKind: 'time',
      athleteId: this.athlete.id,
      athleteName: this.athlete.name,
      species: this.athlete.species,
      nationality: this.athlete.nationality,
      metric: this.elapsed,
      score,
      reward,
      rank,
      standings,
      topSpeed: this.topSpeed,
      perfectTaps: this.perfectTaps,
    };

    this.bigText.setText(t('game.finish')).setFontSize(Math.round(64 * this.uiS)).setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    this.time.delayedCall(2200, () => this.finishDiscipline(result));
  }

  private confetti(): void {
    const x = this.rig.container.x;
    const burst = this.add.particles(0, 0, 'confetti', {
      x, y: this.groundY - 260,
      lifespan: 1600,
      speed: { min: 180, max: 420 },
      angle: { min: 200, max: 340 },
      gravityY: 520,
      scale: { start: 1, end: 0.4 },
      rotate: { start: 0, end: 360 },
      tint: [0xff5d6c, 0xffd24a, 0x59d2ff, 0x4ade80, 0xff7a59],
      emitting: false,
    });
    burst.explode(120);
    this.time.delayedCall(1800, () => burst.destroy());
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch {
        /* not supported */
      }
    }
  }
}
